// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { PopupsName, TitleVariant } from '@shared/ui-kit/components';
import { Heading } from '@shared/ui-kit/components';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { type FC, type PropsWithChildren, type ReactNode, useMemo } from 'react';

interface SectionContainerProps {
  className?: string;
  testId?: string;
  variant?: TitleVariant;
  title?: string | ReactNode;
  actions?: ReactNode;
  popupName?: PopupsName;
  headerClass?: string;
  tooltip?: boolean;
}

export const SectionContainer: FC<PropsWithChildren<SectionContainerProps>> = ({
  children,
  className = '',
  headerClass = '',
  testId = 'default',
  variant = 'h2',
  actions,
  title,
  popupName,
  tooltip,
}) => {
  const _headerClass = useMemo(
    () =>
      classNames(
        'flex items-center',
        {
          'justify-end': !title,
          'justify-between': !!title && !!actions,
        },
        headerClass,
      ),
    [title, actions, headerClass],
  );

  return (
    <section
      data-testid={`${testId}-section`}
      className={`relative min-w-full rounded-lg bg-section p-3 md:p-6 ${className}`}
    >
      {(title || actions) && (
        <div data-testid="header" className={_headerClass}>
          {title && (
            <Heading
              title={title}
              variant={variant}
              popupName={popupName}
              tooltip={tooltip}
              className="!font-medium"
            />
          )}
          {actions}
        </div>
      )}

      {children}
    </section>
  );
};
